import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import TopbarInner from "../components/topbar/TopbarInner";
import { useDarkMode } from "../hooks/useDarkMode";
import { Helmet } from "react-helmet-async";
import { goBack } from "../utils/goBack";

const SCIENCE_SECTIONS = [
  {
    id: "respiration-lente",
    title: "Respiration lente et cohérence cardiaque",
    description: (
      <>
        Respirer à environ <strong>6 cycles par minute</strong> maximise la variabilité de la fréquence cardiaque, un marqueur clé de la santé du système nerveux autonome et de la résilience au stress.
      </>
    ),
    sources: [
      {
        authors: "Sevoz-Couche & Laborde (2022)",
        title: "Heart rate variability and slow-paced breathing: when coherence meets resonance",
        publication: "Neuroscience & Biobehavioral Reviews, 135, 104576.",
        label: "Lire l’étude",
        href: "https://www.sciencedirect.com/science/article/abs/pii/S0149763422000653",
      },
      {
        authors: "Laborde et al. (2022)",
        title: "Effects of voluntary slow breathing on heart rate and heart rate variability: A systematic review and meta-analysis",
        publication: "Neuroscience & Biobehavioral Reviews.",
        label: "Lire la méta-analyse",
        href: "https://www.sciencedirect.com/science/article/abs/pii/S0149763422002007",
      },
      {
        authors: "Noble & Hochman (2019)",
        title: "Pulmonary Afferent Activity Patterns During Slow, Deep Breathing",
        publication: "Frontiers in Neuroscience.",
        label: "Consulter PubMed",
        href: "https://www.ncbi.nlm.nih.gov/pmc/articles/PMC6753868/",
      },
    ],
  },
  {
    id: "soupir-physiologique",
    title: "Soupir physiologique",
    description: (
      <>
        Des travaux menés par les équipes du Dr Andrew Huberman et du Dr David Spiegel indiquent que <strong>5 minutes par jour</strong> de soupir physiologique cyclique peuvent réduire l’anxiété et améliorer l’humeur.
      </>
    ),
    sources: [
      {
        authors: "Balban, Neri, Kogon et al. (2023)",
        title: "Brief structured respiration practices enhance mood and reduce physiological arousal",
        publication: "Cell Reports Medicine, 4(1), 100895.",
        label: "Lire l’étude",
        href: "https://www.cell.com/cell-reports-medicine/fulltext/S2666-3791(22)00474-8",
      },
      {
        authors: "Stanford Medicine News (2023)",
        title: "Cyclic sighing can help breathe away anxiety",
        publication: "",
        label: "Lire l’article",
        href: "https://med.stanford.edu/news/insights/2023/02/cyclic-sighing-can-help-breathe-away-anxiety.html",
      },
    ],
  },
  {
    id: "respiration-478",
    title: "Respiration 4-7-8",
    description: (
      <>
        La technique 4-7-8, popularisée par le Dr Andrew Weil, fait l’objet d’études évaluant notamment ses effets sur l’anxiété, le sommeil et certains marqueurs physiologiques.
      </>
    ),
    sources: [
      {
        authors: "Vierra et al. (2022)",
        title: "Effects of sleep deprivation and 4-7-8 breathing control on heart rate variability, blood pressure, blood glucose, and endothelial function",
        publication: "Physiological Reports.",
        label: "Consulter PubMed",
        href: "https://pubmed.ncbi.nlm.nih.gov/35808855/",
      },
      {
        authors: "Scoping Review (2025)",
        title: "Exploring 4-7-8 Breathing for Stress Relief and Improved Quality of Life in Chronic and Degenerative Diseases",
        publication: "",
        label: "Consulter la publication",
        href: "https://www.researchgate.net/publication/394625657",
      },
    ],
  },
  {
    id: "battements-binauraux",
    title: "Battements binauraux",
    description: (
      <>
        Les battements binauraux ont fait l’objet de plusieurs essais cliniques, notamment en contexte préopératoire, avec des résultats étudiés sur l’anxiété et la perception de la douleur.
      </>
    ),
    sources: [
      {
        authors: "Méta-analyse (2025)",
        title: "Binaural beats for perioperative anxiety and pain: A systematic review and meta-analysis",
        publication: "",
        label: "Lire la méta-analyse",
        href: "https://www.sciencedirect.com/science/article/pii/S096522992500175X",
      },
      {
        authors: "Wiwatwongwana et al. (2016)",
        title: "Essai clinique randomisé en chirurgie de la cataracte",
        publication: "Eye.",
        label: "Consulter PubMed",
        href: "https://pubmed.ncbi.nlm.nih.gov/27740618/",
      },
      {
        authors: "Opartpunyasarn et al. (2022)",
        title: "Essai clinique randomisé lors d’une bronchoscopie",
        publication: "",
        label: "Consulter PMC",
        href: "https://www.ncbi.nlm.nih.gov/pmc/articles/PMC9276398/",
      },
      {
        authors: "Aparecido-Kanzler et al. (2021)",
        title: "Effects of binaural beats and isochronic tones on brain wave modulation",
        publication: "",
        label: "Consulter SciELO",
        href: "https://www.scielo.org.mx/scielo.php?script=sci_arttext&pid=S1665-50442021000600238",
      },
    ],
  },
  {
    id: "sons-naturels",
    title: "Sons de la nature",
    description: (
      <>
        Les paysages sonores naturels peuvent faciliter la récupération du système nerveux après un stress et contribuer à un état plus calme.
      </>
    ),
    sources: [
      {
        authors: "Kumpulainen et al. (2025)",
        title: "Enhancing Psychophysiological Well-Being Through Nature-Based Soundscapes",
        publication: "",
        label: "Consulter Wiley",
        href: "https://onlinelibrary.wiley.com/doi/10.1111/psyp.14760",
      },
      {
        authors: "Alvarsson, Wiens & Nilsson (2010)",
        title: "Stress Recovery during Exposure to Nature Sound and Environmental Noise",
        publication: "",
        label: "Consulter PMC",
        href: "https://www.ncbi.nlm.nih.gov/pmc/articles/PMC2872309/",
      },
      {
        authors: "Buxton et al. (2021)",
        title: "A synthesis of health benefits of natural sounds and their distribution in national parks",
        publication: "PNAS.",
        label: "Lire l’étude",
        href: "https://www.pnas.org/doi/10.1073/pnas.2013097118",
      },
    ],
  },
];

export default function Science({ showThemeDropdown = true, isPremium = false, longestStreak = 0 }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode, toggleDarkMode } = useDarkMode();

  useEffect(() => {
    if (!showThemeDropdown) {
      document.documentElement.setAttribute("data-theme", "light");
    }
  }, [showThemeDropdown, isDarkMode]);

const handleBack = () => goBack(navigate, typeof location.state?.from === "string" ? location.state.from : "/");

  return (
    <>
      <Helmet>
        <title>Les études derrière Harmonauria — respiration lente et cohérence cardiaque</title>
        <meta name="description" content="Les travaux scientifiques qui éclairent Harmonauria : respiration lente à 6 cycles par minute, variabilité de la fréquence cardiaque et système nerveux autonome." />
        <link rel="canonical" href="https://harmonauria.fr/science" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Les études derrière Harmonauria" />
        <meta property="og:description" content="Les recherches sur la respiration lente, la cohérence cardiaque et les sons." />
        <meta property="og:url" content="https://harmonauria.fr/science" />
      </Helmet>

    <div className={`science-shell ${showThemeDropdown ? "science-shell--profile" : "science-shell--public"}`}>
      <div className="header-floating app-header--compact">
<TopbarInner
  title="Science"
  titleColor={showThemeDropdown && isDarkMode ? undefined : "#36495B"}
  onBack={handleBack}
  onLogoClick={() => navigate("/")}
  isDarkMode={isDarkMode}
  toggleDarkMode={toggleDarkMode}
  showThemeDropdown={showThemeDropdown}
  isPremium={isPremium}
  longestStreak={longestStreak}
/>
      </div>

      <main className="science-page">
        <header className="science-hero">
          <p className="science-hero__eyebrow">Recherche et transparence</p>

          <h1 className="science-hero__title">Comprendre ce qui soutient votre calme</h1>

          <p className="science-hero__description">Harmonauria s’appuie sur des pratiques étudiées par la recherche scientifique. Cette page rassemble les principales publications qui éclairent notre approche de la respiration et des paysages sonores.</p>
        </header>

        <div className="science-sections">
          {SCIENCE_SECTIONS.map((section, index) => (
            <section
              key={section.id}
              className="science-section"
              aria-labelledby={`science-title-${section.id}`}
            >
              <div className="science-section__header">
                <span
                  className="science-section__number"
                  aria-hidden="true"
                >
                  {String(index + 1).padStart(2, "0")}
                </span>

                <div className="science-section__intro">
                  <h2
                    id={`science-title-${section.id}`}
                    className="science-section__title"
                  >
                    {section.title}
                  </h2>

                  <p className="science-section__description">{section.description}</p>
                </div>
              </div>

              <ul className="science-sources">
                {section.sources.map((source) => (
                  <li
                    key={`${section.id}-${source.href}`}
                    className="science-source"
                  >
                    <p className="science-source__reference">
                      <strong>{source.authors}</strong>
                      {" — "}
                      <em>{source.title}</em>
                      {source.publication ? `, ${source.publication}` : "."}
                    </p>

                    <a
                      href={source.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="science-source__link"
                    >
                      <span>{source.label}</span>
                      <span aria-hidden="true">↗</span>
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        <section
          className="science-transparency"
          aria-labelledby="science-transparency-title"
        >
          <p className="science-transparency__eyebrow">Notre engagement</p>

          <h2
            id="science-transparency-title"
            className="science-transparency__title"
          >
            Présenter clairement ce qui est établi
          </h2>

          <p className="science-transparency__text">Certaines pratiques, comme les tons isochrones ou les fréquences dites « Solfeggio », restent moins documentées scientifiquement. Nous les proposons comme une exploration sensorielle, sans prétendre à des effets cliniques démontrés.</p>

          <p className="science-transparency__text">Harmonauria ne remplace pas un avis médical. En cas de trouble persistant du sommeil, d’anxiété ou de dépression, parlez-en à un professionnel de santé.</p>
        </section>
      </main>

      <style>{`
.science-shell {
  position: relative;
  z-index: 1;
  width: 100%;
  min-height: 100vh;
  min-height: 100dvh;
  background: transparent;
  color: #203e3e;
}

.science-shell--public {
  background: #ffffff;
  color: #203e3e;
  color-scheme: light;
}

.science-shell *,
.science-shell *::before,
.science-shell *::after {
  box-sizing: border-box;
}

.science-page {
  width: min(100%, 1040px);
  margin: 0 auto;
  padding: calc(var(--topbar-height, 56px) + env(safe-area-inset-top, 0px) + 58px) 24px max(84px, calc(env(safe-area-inset-bottom, 0px) + 60px));
}

.science-hero {
  width: min(100%, 760px);
  margin: 0 auto clamp(54px, 8vw, 84px);
  text-align: center;
}

.science-hero__eyebrow {
  margin: 0 0 14px;
  color: #61837a;
  font-family: var(--font-sans, "DM Sans", sans-serif);
  font-size: 0.72rem;
  font-weight: 900;
  line-height: 1;
  letter-spacing: 0.13em;
  text-transform: uppercase;
}

.science-hero__title {
  margin: 0;
  color: #203e3e;
  font-family: var(--font-display, "Nunito", sans-serif);
  font-size: clamp(2.25rem, 6vw, 4rem);
  font-weight: 850;
  line-height: 1.04;
  letter-spacing: -0.05em;
  text-wrap: balance;
}

.science-hero__description {
  max-width: 680px;
  margin: 20px auto 0;
  color: #506866;
  font-family: var(--font-sans, "DM Sans", sans-serif);
  font-size: clamp(0.98rem, 2vw, 1.08rem);
  font-weight: 500;
  line-height: 1.72;
  text-wrap: balance;
}

.science-sections {
  display: grid;
  gap: clamp(24px, 4vw, 34px);
}

.science-section {
  width: 100%;
  padding: 0 4px clamp(32px, 5vw, 42px);
  border-bottom: 1px solid rgba(97, 131, 122, 0.16);
  background: transparent;
  box-shadow: none;
}

.science-section__header {
  display: grid;
  grid-template-columns: 46px minmax(0, 1fr);
  align-items: start;
  gap: 18px;
  margin-bottom: 26px;
}

.science-section__number {
  width: 46px;
  height: 46px;
  display: grid;
  place-items: center;
  border: 1px solid rgba(97, 131, 122, 0.16);
  border-radius: 15px;
  background: rgba(203, 226, 219, 0.3);
  color: #61837a;
  font-family: var(--font-sans, "DM Sans", sans-serif);
  font-size: 0.75rem;
  font-weight: 900;
  letter-spacing: 0.05em;
}

.science-section__intro {
  min-width: 0;
}

.science-section__title {
  margin: 0;
  color: #203e3e;
  font-family: var(--font-display, "Nunito", sans-serif);
  font-size: clamp(1.4rem, 3vw, 1.85rem);
  font-weight: 850;
  line-height: 1.15;
  letter-spacing: -0.035em;
}

.science-section__description {
  margin: 10px 0 0;
  color: #506866;
  font-family: var(--font-sans, "DM Sans", sans-serif);
  font-size: 0.96rem;
  font-weight: 500;
  line-height: 1.65;
}

.science-section__description strong {
  color: #405b54;
  font-weight: 800;
}

.science-sources {
  margin: 0;
  padding: 0;
  display: grid;
  gap: 11px;
  list-style: none;
}

.science-source {
  min-width: 0;
  padding: 14px 2px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 18px;
  border: 0;
  border-bottom: 1px solid rgba(97, 131, 122, 0.1);
  border-radius: 0;
  background: transparent;
  transition: color 180ms ease;
}

.science-source:last-child {
  border-bottom: 0;
}

.science-source__reference {
  min-width: 0;
  margin: 0;
  color: #506866;
  font-family: var(--font-sans, "DM Sans", sans-serif);
  font-size: 0.86rem;
  font-weight: 500;
  line-height: 1.55;
}

.science-source__reference strong {
  color: #294b48;
  font-weight: 800;
}

.science-source__reference em {
  color: #405b54;
}

.science-source__link {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  color: #61837a;
  font-family: var(--font-sans, "DM Sans", sans-serif);
  font-size: 0.8rem;
  font-weight: 850;
  text-decoration: none;
  white-space: nowrap;
}

.science-source__link:hover {
  color: #203e3e;
  text-decoration: underline;
  text-underline-offset: 4px;
}

.science-transparency {
  margin-top: clamp(34px, 5vw, 50px);
  padding: clamp(26px, 4vw, 34px);
  border: 1px solid rgba(97, 131, 122, 0.18);
  border-radius: 22px;
  background: rgba(245, 248, 247, 0.88);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

.science-transparency__eyebrow {
  margin: 0 0 11px;
  color: #61837a;
  font-family: var(--font-sans, "DM Sans", sans-serif);
  font-size: 0.7rem;
  font-weight: 900;
  line-height: 1;
  letter-spacing: 0.13em;
  text-transform: uppercase;
}

.science-transparency__title {
  margin: 0 0 18px;
  color: #203e3e;
  font-family: var(--font-display, "Nunito", sans-serif);
  font-size: clamp(1.45rem, 3vw, 1.9rem);
  font-weight: 850;
  line-height: 1.15;
  letter-spacing: -0.035em;
}

.science-transparency__text {
  margin: 0;
  color: #506866;
  font-family: var(--font-sans, "DM Sans", sans-serif);
  font-size: 0.94rem;
  font-weight: 500;
  line-height: 1.65;
}

.science-transparency__text + .science-transparency__text {
  margin-top: 14px;
}

@media (max-width: 700px) {
  .science-page {
    padding-top: calc(var(--topbar-height, 56px) + env(safe-area-inset-top, 0px) + 40px);
    padding-right: 16px;
    padding-left: 16px;
  }

  .science-hero {
    margin-bottom: 46px;
  }

.science-section {
  padding: 0 2px 32px;
}

  .science-section__header {
    grid-template-columns: 40px minmax(0, 1fr);
    gap: 14px;
    margin-bottom: 22px;
  }

  .science-section__number {
    width: 40px;
    height: 40px;
    border-radius: 13px;
  }

.science-source {
  grid-template-columns: 1fr;
  gap: 8px;
  padding: 14px 0;
}

  .science-source__link {
    width: fit-content;
  }

  .science-transparency {
    padding: 24px 20px;
    border-radius: 22px;
  }
}

@media (max-width: 430px) {
  .science-hero__title {
    font-size: clamp(2rem, 10vw, 2.55rem);
  }

  .science-section__title {
    font-size: 1.32rem;
  }

  .science-section__description {
    font-size: 0.9rem;
  }

  .science-source {
    padding: 15px;
  }

  .science-source__reference {
    font-size: 0.82rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .science-source {
    transition: none;
  }
}
      `}</style>
    </div>
    </>
  );
}